/**
 * @fileoverview Format user-visible error strings with stable numeric codes (admin vs non-admin).
 * @purpose Align API and UI with docs/ERROR_CODES.md (6-digit code = catalogCode × 1000 + throw id).
 * @usage `formatErrorMessageWithId`, `formatApiErrorJsonBody`, `extractErrorId`, `calculateSixDigitCode` from `@korvo/core/lib/error-format-helpers`.
 * @ownership Platform / error contracts
 * @restrictions Must stay consistent with `error-ids` registry and KorvoError composite ids (`BASE:unique`).
 */

import { getErrorCatalogCode, getErrorUniqueThrowId } from './error-ids';

export function extractErrorId(error: unknown): string | undefined {
  if (error !== null && typeof error === 'object' && 'id' in error) {
    const v = (error as { id?: unknown }).id;
    return typeof v === 'string' && v.trim() !== '' ? v : undefined;
  }
  return undefined;
}

export type ParsedErrorId = {
  baseCode: string;
  uniqueId: number;
};

/**
 * Parse registry id: composite `BASE:unique` or `BASE_uniqueSuffix` with numeric tail (unique from last segment).
 */
export function parseErrorId(errorIdString: string): ParsedErrorId {
  const sep = ':';
  if (errorIdString.includes(sep)) {
    const idx = errorIdString.indexOf(sep);
    const baseCode = errorIdString.slice(0, idx);
    const num = parseInt(errorIdString.slice(idx + 1), 10);
    const uniqueId = !Number.isNaN(num) && num >= 1 && num <= 999 ? num : 1;
    return { baseCode, uniqueId };
  }
  const lastUnderscore = errorIdString.lastIndexOf('_');
  if (lastUnderscore > 0) {
    const tail = errorIdString.slice(lastUnderscore + 1);
    const num = parseInt(tail, 10);
    if (!Number.isNaN(num) && num >= 1 && num <= 999) {
      return { baseCode: errorIdString.slice(0, lastUnderscore), uniqueId: num };
    }
  }
  return { baseCode: errorIdString, uniqueId: getErrorUniqueThrowId(errorIdString) };
}

export function calculateSixDigitCode(errorIdString: string): number {
  const catalogCode = getErrorCatalogCode(errorIdString);
  const uniqueId = getErrorUniqueThrowId(errorIdString);
  return catalogCode * 1000 + uniqueId;
}

export function formatErrorMessageWithId(
  message: string,
  error: unknown,
  options: { isAdmin: boolean },
): string {
  const errorIdString = extractErrorId(error);
  if (errorIdString === undefined) {
    return message;
  }
  const six = calculateSixDigitCode(errorIdString);
  if (options.isAdmin) {
    return `${message} Error ${errorIdString} ${six}`;
  }
  return `${message} Error ${six}`;
}

export type ErrorFormatHelper = {
  extractErrorId: typeof extractErrorId;
  parseErrorId: typeof parseErrorId;
  calculateSixDigitCode: typeof calculateSixDigitCode;
  formatErrorMessageWithId: typeof formatErrorMessageWithId;
};

export const defaultErrorFormatHelper: ErrorFormatHelper = {
  extractErrorId,
  parseErrorId,
  calculateSixDigitCode,
  formatErrorMessageWithId,
};

export function createErrorFormatHelper(): ErrorFormatHelper {
  return { ...defaultErrorFormatHelper };
}

function parseFinitePositiveInt(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return undefined;
}

/**
 * Build a single user-visible line from an `apiErrorResponse`-shaped JSON body (`error`, numeric `code` catalog, numeric `errorId` throw id).
 * Appends **Error NNNNNN** when both ids are present, matching docs/ERROR_CODES.md (six-digit = catalogCode × 1000 + unique throw id).
 */
export function formatApiErrorJsonBody(
  data: { error?: unknown; code?: unknown; errorId?: unknown },
  fallbackMessage: string,
): string {
  const base =
    typeof data.error === 'string' && data.error.trim() !== '' ? data.error.trim() : fallbackMessage;
  const catalogCode = parseFinitePositiveInt(data.code);
  const uniqueId = parseFinitePositiveInt(data.errorId);
  if (
    catalogCode !== undefined &&
    uniqueId !== undefined &&
    catalogCode >= 100 &&
    catalogCode <= 999 &&
    uniqueId >= 1 &&
    uniqueId <= 999
  ) {
    const six = catalogCode * 1000 + uniqueId;
    if (base.includes(`Error ${six}`)) {
      return base;
    }
    return `${base} Error ${six}`;
  }
  return base;
}
